/**
 * The vertical registry, and what an operator may do to it.
 *
 * The nine we sell are JSON files: reviewed, in git, deployed with the code.
 * This screen does not pretend otherwise — a file-backed vertical is labelled as
 * such and cannot be deleted here. What it adds is the tenth: a vertical needed
 * before anybody has time to ship one, and the ability to override a shipped
 * bundle without waiting for a release.
 *
 * The tenant count is the most important column. It is what tells an operator
 * whether retiring a vertical is free or is about to strip somebody's modules,
 * terminology and navigation out from under them.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Layers3, Loader2, Plus, RotateCcw, Save, Trash2 } from 'lucide-react';
import { api } from '../services/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CardSkeleton, EmptyState } from '../components/common/States';
import { getErrorMessage } from '../lib/utils';

const BLANK = {
  key: '', label: '', is_active: true,
  definition: {
    industry_suite: '', terminology: {}, modules: { enabled: [], hidden: [] },
    feature_flags: {}, roles: { labels: {} }, navigation: [],
    dashboards: [], workflows: [], marketplace: { recommended: [], categories: [] },
  },
};

const SuperadminVerticalsPage = () => {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);   // { key, label, is_active, json }
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/api/vertical/definitions');
      setRows(data.verticals || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not load the vertical registry'));
      setRows([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = async (key) => {
    if (!key) {
      setEditing({ ...BLANK, json: JSON.stringify(BLANK.definition, null, 2), isNew: true });
      return;
    }
    try {
      const { data } = await api.get(`/api/vertical/definitions/${key}`);
      const { key: _k, label, listed, source: _s, ...rest } = data.definition;
      setEditing({
        key: data.key,
        label: label || data.key,
        is_active: listed !== false,
        json: JSON.stringify(rest, null, 2),
        isNew: false,
      });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not open that vertical'));
    }
  };

  const save = async () => {
    let definition;
    try {
      definition = JSON.parse(editing.json);
    } catch (error) {
      // Reported here rather than server-side: a JSON syntax error is a typo in
      // this textarea, and the operator needs the position, not a 422.
      toast.error(`That is not valid JSON — ${error.message}`);
      return;
    }
    setSaving(true);
    try {
      await api.put(`/api/vertical/definitions/${editing.key.trim().toLowerCase()}`, {
        key: editing.key.trim().toLowerCase(),
        label: editing.label,
        is_active: editing.is_active,
        definition,
      });
      toast.success(`Saved ${editing.key}`);
      setEditing(null);
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not save'));
    } finally { setSaving(false); }
  };

  const remove = async (row) => {
    const restores = row.overrides_file;
    const question = restores
      ? `Drop the override on "${row.key}" and go back to the version shipped with the product?`
      : `Delete the vertical "${row.key}"? This cannot be undone.`;
    if (!window.confirm(question)) return;
    try {
      const { data } = await api.delete(`/api/vertical/definitions/${row.key}`);
      toast.success(data.restored_file ? 'Override dropped — the shipped version is live again' : 'Deleted');
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not delete'));
    }
  };

  if (rows === null) return <div className="p-6 space-y-3"><CardSkeleton rows={6} /></div>;

  return (
    <div className="p-6 space-y-4">
      <header className="flex flex-wrap items-start gap-4">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary">
            <Layers3 className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Verticals</h1>
            <p className="text-sm text-muted-foreground">
              A tenant picks one. It decides their modules, terminology, navigation, role names and dashboards.
            </p>
          </div>
        </div>
        <Button className="ml-auto" onClick={() => open(null)}>
          <Plus className="h-4 w-4 mr-1.5" />New vertical
        </Button>
      </header>

      {editing ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {editing.isNew ? 'New vertical' : `Editing ${editing.key}`}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="v-key">Key</Label>
                <Input id="v-key" value={editing.key} disabled={!editing.isNew}
                  placeholder="logistics"
                  onChange={(e) => setEditing((p) => ({ ...p, key: e.target.value }))} />
                <p className="text-xs text-muted-foreground">
                  {editing.isNew
                    ? 'Lowercase letters, digits and underscores. This is what tenant rows store.'
                    : 'The key is what tenants are stored against, so it cannot be renamed.'}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="v-label">Label</Label>
                <Input id="v-label" value={editing.label} placeholder="Logistics"
                  onChange={(e) => setEditing((p) => ({ ...p, label: e.target.value }))} />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="accent-primary" checked={editing.is_active}
                onChange={(e) => setEditing((p) => ({ ...p, is_active: e.target.checked }))} />
              Offer this vertical when creating or editing a tenant
            </label>

            <div className="space-y-1.5">
              <Label htmlFor="v-json">Bundle</Label>
              <textarea id="v-json" rows={20} spellCheck={false}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-xs"
                value={editing.json}
                onChange={(e) => setEditing((p) => ({ ...p, json: e.target.value }))} />
              <p className="text-xs text-muted-foreground">
                Same shape as <code>backend/verticals/*.json</code>. Every module named here must be one
                the platform sells, or the vertical renders menus that refuse the moment they are clicked.
              </p>
            </div>

            <div className="flex gap-2">
              <Button onClick={save} disabled={saving || !editing.key || !editing.label}>
                {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                Save
              </Button>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <EmptyState icon={Layers3} title="No verticals" description="The registry is empty." />
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="text-left font-medium p-3">Vertical</th>
                  <th className="text-left font-medium p-3">Source</th>
                  <th className="text-left font-medium p-3">Modules</th>
                  <th className="text-left font-medium p-3">Tenants</th>
                  <th className="text-right font-medium p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key} className="border-b border-border hover:bg-muted/30">
                    <td className="p-3">
                      <div className="font-medium text-foreground">{row.label}</div>
                      <div className="text-xs text-muted-foreground font-mono">{row.key}</div>
                    </td>
                    <td className="p-3">
                      {row.source === 'file'
                        ? <Badge variant="info">shipped</Badge>
                        : <Badge variant="soft">{row.overrides_file ? 'override' : 'custom'}</Badge>}
                      {!row.listed ? <Badge variant="outline" className="ml-1">hidden</Badge> : null}
                    </td>
                    <td className="p-3 tabular-nums text-muted-foreground">{row.modules_enabled}</td>
                    <td className="p-3 tabular-nums">
                      {row.tenant_count > 0
                        ? <Badge variant="success">{row.tenant_count}</Badge>
                        : <span className="text-muted-foreground">0</span>}
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => open(row.key)}
                        disabled={row.key === 'generic'}>
                        {row.source === 'file' ? 'Override' : 'Edit'}
                      </Button>
                      {row.source === 'database' ? (
                        <Button size="sm" variant="outline" className="ml-1 text-danger"
                          onClick={() => remove(row)}
                          title={row.overrides_file ? 'Drop the override' : 'Delete'}>
                          {row.overrides_file ? <RotateCcw className="h-3 w-3" /> : <Trash2 className="h-3 w-3" />}
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperadminVerticalsPage;
